import * as THREE from 'three';
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export class App {
    private canvas: HTMLCanvasElement;
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private renderer: THREE.WebGLRenderer;
    private width: number = 1;
    private height: number = 1;
    private controls?: OrbitControls;

    private time: number = 0;
    private isPaused: boolean = false;

    private readonly numBalls = 20000;
    private readonly ballRadius = 0.03;
    private ballCloud?: THREE.InstancedMesh;
    private positions: Float32Array;
    private readonly rotationSpeed = 0.3 / 1000; // radians per millisecond
    private readonly planeSize = 5; // Size of cross-section planes
    private readonly planeOpacity = 0.2; // Opacity of cross-section planes

    constructor(canvasId: string) {
        this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;
        if (!this.canvas) {
            throw new Error(`Canvas with id ${canvasId} not found`);
        }

        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0xffffff);
        this.camera = new THREE.PerspectiveCamera(60, 2, 0.1, 1000);
        this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });

        this.positions = new Float32Array(this.numBalls * 3);
        this.generateBallPositions();

        this.resizeCanvasToDisplaySize();
    }

    private generateBallPositions() {
        // Generate random positions using standard normal distribution
        for (let i = 0; i < this.numBalls; i++) {
            const idx = i * 3;
            // Box-Muller transform for normal distribution
            const generateNormal = () => {
                const u1 = Math.random();
                const u2 = Math.random();
                return Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
            };

            this.positions[idx] = generateNormal();     // x
            this.positions[idx + 1] = generateNormal(); // y
            this.positions[idx + 2] = generateNormal(); // z
        }
    }

    init() {
        // Create the instanced mesh
        const geometry = new THREE.SphereGeometry(this.ballRadius, 12, 12);

        // ShaderMaterial that replicates MeshStandardMaterial functionality
        const vertexShader = `
            varying vec3 vNormal;
            varying vec3 vViewPosition;

            void main() {
                vNormal = normalize(normalMatrix * (instanceMatrix * vec4(normal, 0.0)).xyz);
                // vec4 mvPosition = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
                vec4 mp = modelMatrix * instanceMatrix * vec4(position, 1.0);
                if (mp.x > 0.0 && mp.y > 0.0 && mp.z > 0.0) {
                    if (mp.x < 0.031) {
                        mp.x = 0.0;
                        vNormal = vec3(1.0, 0.0, 0.0);
                    }
                    if (mp.y < 0.031) {
                        mp.y = 0.0;
                        vNormal = vec3(0.0, 1.0, 0.0);
                    }
                    if (mp.z < 0.031) {
                        mp.z = 0.0;
                        vNormal = vec3(0.0, 0.0, 1.0);
                    }
                }
                vec4 mvPosition = viewMatrix * mp;
                vViewPosition = -mvPosition.xyz;
                gl_Position = projectionMatrix * mvPosition;
            }
        `;

        const fragmentShader = `
            uniform vec3 diffuse;
            uniform float roughness;
            uniform float metalness;

            uniform vec3 hemiLightSkyColor;
            uniform vec3 hemiLightGroundColor;
            uniform vec3 hemiLightDirection;

            uniform vec3 directionalLightColor;
            uniform vec3 directionalLightDirection;

            varying vec3 vNormal;
            varying vec3 vViewPosition;

            void main() {
                vec3 normal = normalize(vNormal);

                // Hemisphere light calculation
                float hemiDiffuseWeight = 0.5 * dot(normal, hemiLightDirection) + 0.5;
                vec3 hemiColor = mix(hemiLightGroundColor, hemiLightSkyColor, hemiDiffuseWeight);

                // Directional light calculation
                float directionalDiffuseWeight = max(dot(normal, directionalLightDirection), 0.0);
                vec3 directionalColor = directionalLightColor * directionalDiffuseWeight;

                // Combine lighting with material properties
                vec3 outgoingLight = diffuse * (hemiColor + directionalColor);

                // Apply a simple approximation of roughness/metalness
                // (In a real PBR shader, this would be much more complex)
                float glossiness = 1.0 - roughness;
                vec3 viewDirection = normalize(vViewPosition);
                vec3 halfVector;

                // Simple specular for directional light
                halfVector = normalize(directionalLightDirection + viewDirection);
                float directionalSpecular = pow(max(dot(normal, halfVector), 0.0), 16.0 * glossiness);

                // Mix in specular based on metalness
                outgoingLight += directionalLightColor * directionalSpecular * metalness;

                gl_FragColor = vec4(outgoingLight, 1.0);
            }
        `;

        const material = new THREE.ShaderMaterial({
            uniforms: {
                diffuse: { value: new THREE.Color(0x77ff00) },
                roughness: { value: 0.5 },
                metalness: { value: 0.2 },
                hemiLightSkyColor: { value: new THREE.Color(0xffffbb) },
                hemiLightGroundColor: { value: new THREE.Color(0x080820) },
                hemiLightDirection: { value: new THREE.Vector3(0, 1, 0) },
                directionalLightColor: { value: new THREE.Color(0x555555) },
                directionalLightDirection: { value: new THREE.Vector3(1, 0.75, 0.5).normalize() }
            },
            vertexShader: vertexShader,
            fragmentShader: fragmentShader
        });

        this.ballCloud = new THREE.InstancedMesh(geometry, material, this.numBalls);

        // Initialize all matrices
        const matrix = new THREE.Matrix4();
        for (let i = 0; i < this.numBalls; i++) {
            const idx = i * 3;
            matrix.setPosition(
                this.positions[idx],
                this.positions[idx + 1],
                this.positions[idx + 2]
            );
            this.ballCloud.setMatrixAt(i, matrix);
        }
        this.scene.add(this.ballCloud);

        // Add cross-section planes at x=0, y=0, z=0
        // this.addCrossSectionPlanes();

        // Add lighting

        // Hemisphere light with contrasting colors
        // skyColor (from above), groundColor (from below), intensity
        const hemiLight = new THREE.HemisphereLight(0xffffbb, 0x080820, 1);
        hemiLight.position.set(0, 1, 0); // Light from above
        this.scene.add(hemiLight);

        // Add a subtle directional light to enhance the 3D effect
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.5);
        dirLight.position.set(1, 0.75, 0.5);
        this.scene.add(dirLight);

        // Set up camera and controls
        this.camera.position.z = 5;
        this.controls = new OrbitControls(this.camera, this.canvas);
        this.controls.enableDamping = true; // This adds smooth movement to the controls

        window.addEventListener('resize', () => this.resizeCanvasToDisplaySize());

        // Add space key listener for pause/unpause
        window.addEventListener('keydown', (event) => {
            if (event.code === 'Space') {
                this.isPaused = !this.isPaused;
                event.preventDefault(); // Prevent space from scrolling the page
            }
        });
    }

    private resizeCanvasToDisplaySize() {
        const width = Math.floor(this.canvas.clientWidth);
        const height = Math.floor(this.canvas.clientHeight);
        if (this.width !== width || this.height !== height) {
            this.renderer.setSize(width, height, false);
            this.renderer.setPixelRatio(window.devicePixelRatio);
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
            this.width = width;
            this.height = height;
        }
    }

    private updateBallPositions(deltaTime: number) {
        if (!this.ballCloud) return;

        const rotationAngle = this.rotationSpeed * deltaTime;
        const c = Math.cos(rotationAngle);
        const s = Math.sin(rotationAngle);

        // Update positions and instance matrices
        const matrix = new THREE.Matrix4();
        const noRotation = new THREE.Quaternion();
        const scale = new THREE.Vector3(1, 1, 1);  // Default scale
        const zeroScale = new THREE.Vector3(0, 0, 0);  // Zero scale for hidden balls
        for (let i = 0; i < this.numBalls; i++) {
            const idx = i * 3;
            const x = this.positions[idx];
            const z = this.positions[idx + 2];
            this.positions[idx] = c * x + s * z;
            this.positions[idx + 2] = -s * x + c * z;

            // Cut out positive quadrant
            const isHidden = this.positions[idx] > 0
                && this.positions[idx + 1] > 0
                && this.positions[idx + 2] > 0;

            // Set position and scale
            matrix.compose(
                new THREE.Vector3(
                    this.positions[idx],
                    this.positions[idx + 1],
                    this.positions[idx + 2]
                ),
                noRotation,
                isHidden ? zeroScale : scale
            );

            this.ballCloud.setMatrixAt(i, matrix);
        }

        this.ballCloud.instanceMatrix.needsUpdate = true;
    }

    private addCrossSectionPlanes() {
        // Create semi-transparent material for the planes
        const planeMaterial = new THREE.MeshBasicMaterial({
            color: 0x3366ff,
            transparent: true,
            opacity: this.planeOpacity,
            side: THREE.DoubleSide,
        });

        // Create YZ plane (x = 0)
        const yzPlaneGeometry = new THREE.PlaneGeometry(this.planeSize, this.planeSize);
        const yzPlane = new THREE.Mesh(yzPlaneGeometry, planeMaterial);
        yzPlane.rotation.y = Math.PI / 2; // Rotate to face x-axis
        yzPlane.position.set(0, 0, 0);
        this.scene.add(yzPlane);

        // Create XZ plane (y = 0)
        const xzPlaneGeometry = new THREE.PlaneGeometry(this.planeSize, this.planeSize);
        const xzPlane = new THREE.Mesh(xzPlaneGeometry, planeMaterial);
        xzPlane.rotation.x = Math.PI / 2; // Rotate to face y-axis
        xzPlane.position.set(0, 0, 0);
        this.scene.add(xzPlane);

        // Create XY plane (z = 0)
        const xyPlaneGeometry = new THREE.PlaneGeometry(this.planeSize, this.planeSize);
        const xyPlane = new THREE.Mesh(xyPlaneGeometry, planeMaterial);
        xyPlane.position.set(0, 0, 0);
        this.scene.add(xyPlane);
    }

    private animate(time: number) {
        requestAnimationFrame(this.animate.bind(this));

        if (!this.isPaused) {
            this.updateBallPositions(time - this.time);
        }
        this.time = time;

        this.controls?.update(); // Required if controls.enableDamping = true
        this.renderer.render(this.scene, this.camera);
    }

    startAnimation() {
        this.animate(0);
    }
}
